import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Clock, CheckCircle, XCircle, LogOut, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  enfermeiro: "Enfermeiro(a)",
  tecnico: "Técnico(a) de Enfermagem",
  acs: "Agente Comunitário de Saúde",
  medico: "Médico(a)",
  coordenador: "Gestor(a) de Unidade / Coordenador(a) APS",
  secretario: "Secretário(a) de Saúde",
  especialista: "Especialista de Referência",
};

export default function PendingApprovalPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  useEffect(() => {
    if (user?.is_validated) navigate("/dashboard");
    if (!user?.profile_complete) navigate("/profile-setup");
  }, [user, navigate]);

  const handleCheckStatus = async () => {
    setChecking(true);
    try {
      const resp = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(resp.data);
      setLastCheck(new Date());
      if (resp.data.is_validated) {
        navigate("/dashboard");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isRejected = user?.validation_status === "rejeitado";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Icon */}
        <div className="text-center mb-8">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
            isRejected ? "bg-destructive/10" : "bg-yellow-100"
          }`}>
            {isRejected ? (
              <XCircle className="w-10 h-10 text-destructive" />
            ) : (
              <Clock className="w-10 h-10 text-yellow-600" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            {isRejected ? "Cadastro Não Aprovado" : "Cadastro em Análise"}
          </h1>
          <p className="text-muted-foreground text-sm mt-2">
            {isRejected
              ? "Seu cadastro foi rejeitado pelo gestor municipal."
              : "Seu cadastro está aguardando validação pelo gestor municipal."}
          </p>
        </div>

        {/* Info card */}
        <div className="bg-white rounded-2xl border border-border p-6 mb-4">
          <h2 className="text-sm font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
            Dados Enviados
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Nome</span>
              <span className="font-medium text-foreground">{user?.full_name || user?.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Email</span>
              <span className="font-medium text-foreground">{user?.email}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Categoria</span>
              <span className="font-medium text-foreground">
                {CATEGORY_LABELS[user?.professional_category] || user?.professional_category}
              </span>
            </div>
            {user?.council_number && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Registro</span>
                <span className="font-medium text-foreground">{user.council_number}</span>
              </div>
            )}
            {user?.phone && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Telefone</span>
                <span className="font-medium text-foreground">{user.phone}</span>
              </div>
            )}
          </div>
        </div>

        {/* Reason if rejected */}
        {isRejected && user?.validation_reason && (
          <div className="bg-destructive/10 rounded-xl p-4 mb-4">
            <p className="text-sm font-medium text-destructive mb-1">Motivo</p>
            <p className="text-sm text-foreground">{user.validation_reason}</p>
          </div>
        )}

        {/* What to expect */}
        {!isRejected && (
          <div className="bg-secondary/50 rounded-xl p-4 mb-4">
            <h3 className="text-sm font-medium text-foreground mb-2">O que acontece agora?</h3>
            <div className="space-y-2">
              {[
                "O gestor municipal será notificado do seu cadastro",
                "Após validação, você receberá acesso completo ao sistema",
                "Você pode verificar o status clicando em 'Atualizar Status'",
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-muted-foreground">{text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {!isRejected && (
            <button
              onClick={handleCheckStatus}
              disabled={checking}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-all disabled:opacity-60"
              data-testid="check-status-btn"
            >
              <RefreshCw className={`w-4 h-4 ${checking ? "animate-spin" : ""}`} />
              {checking ? "Verificando..." : "Atualizar Status"}
            </button>
          )}
          {lastCheck && (
            <p className="text-xs text-muted-foreground text-center">
              Última verificação: {lastCheck.toLocaleTimeString("pt-BR")}
            </p>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-3.5 border border-border rounded-xl text-sm font-medium hover:bg-secondary transition-all text-muted-foreground"
            data-testid="logout-pending-btn"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>
      </div>
    </div>
  );
}
